import { getRuntimeEnv } from '../runtimeEnv'

const MAX_QUEUE_LENGTH = 50
const MAX_BATCH_SIZE = 20
const MAX_MESSAGE_LENGTH = 500
const FLUSH_DELAY_MS = 1000
const REDACTED = '~REDACTED~'
const WRAPPED = Symbol('citadelClientLogForwarder')

const queue = []
let flushing = false
let flushTimer = null
let flushDelay = null
let suppressForwarding = false
let pageLifecycleFlushInstalled = false

function redactClientLogMessage(message) {
  const bearerPattern = /Bearer\s+[A-Za-z0-9\-._~+/]+=*/gi
  const jwtPattern = /\b[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g
  const secretPattern = /(["']?)(password|access_token|id_token|client_secret)\1(\s*[:=]\s*)("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|[^&\s,"'}]+)/gi

  return message
    .replace(/[\r\n\t]+/g, ' ')
    .replace(bearerPattern, `Bearer ${REDACTED}`)
    .replace(jwtPattern, REDACTED)
    .replace(secretPattern, (_match, quote, key, separator, value) => {
      const valueQuote = value.startsWith('"') ? '"' : value.startsWith("'") ? "'" : ''
      return `${quote}${key}${quote}${separator}${valueQuote}${REDACTED}${valueQuote}`
    })
    .slice(0, MAX_MESSAGE_LENGTH)
}

function isErrorLike(value) {
  if (value instanceof Error) return true
  if (!value || typeof value !== 'object') return false

  const status = value.response && value.response.status
  const hasStatus = status !== undefined && status !== null
  const hasName = typeof value.name === 'string'
  const hasMessage = typeof value.message === 'string'
  return hasStatus && (hasName || hasMessage)
}

function formatError(value) {
  const parts = []
  if (typeof value.name === 'string' && value.name !== '') parts.push(value.name)
  if (typeof value.message === 'string' && value.message !== '') parts.push(value.message)

  const status = value.response && value.response.status
  if (status !== undefined && status !== null && status !== '') parts.push(String(status))
  return parts.join(' ')
}

function formatLogArgument(value) {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') return String(value)
  if (value === null || value === undefined) return String(value)
  if (isErrorLike(value)) return formatError(value)
  return '[object]'
}

function buildLogMessage(args) {
  return redactClientLogMessage(args.map(formatLogArgument).join(' '))
}

function enqueue(level, args) {
  if (suppressForwarding) return

  queue.push({ level, message: buildLogMessage(args) })
  while (queue.length > MAX_QUEUE_LENGTH) queue.shift()
  scheduleFlush()
}

function armFlush(delay) {
  if (flushTimer) clearTimeout(flushTimer)

  flushDelay = delay
  flushTimer = setTimeout(() => {
    flushTimer = null
    flushDelay = null
    flush().catch(flushError => {
      void flushError
    })
  }, delay)
}

function scheduleFlush() {
  const delay = queue.length >= MAX_BATCH_SIZE ? 0 : FLUSH_DELAY_MS
  if (flushTimer && flushDelay !== null && flushDelay <= delay) return

  armFlush(delay)
}

function scheduleRetry() {
  armFlush(FLUSH_DELAY_MS)
}

function flushNow() {
  if (flushTimer) clearTimeout(flushTimer)
  flushTimer = null
  flushDelay = null
  flush().catch(flushError => {
    void flushError
  })
}

function onPageHide() {
  flushNow()
}

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') flushNow()
}

function installPageLifecycleFlush() {
  if (pageLifecycleFlushInstalled || typeof window === 'undefined' || typeof document === 'undefined') return

  pageLifecycleFlushInstalled = true
  window.addEventListener('pagehide', onPageHide)
  document.addEventListener('visibilitychange', onVisibilityChange)
}

function restoreRateLimitedBatch(entries) {
  queue.unshift(...entries)
  while (queue.length > MAX_QUEUE_LENGTH) queue.pop()
}

async function flush() {
  if (flushing) return

  flushing = true
  try {
    while (queue.length > 0) {
      const entries = queue.splice(0, MAX_BATCH_SIZE)
      const apiUrl = getRuntimeEnv('REACT_APP_API_URL')
      if (!apiUrl) {
        queue.length = 0
        return
      }

      let response
      suppressForwarding = true
      try {
        response = await fetch(`${apiUrl}/log/client`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entries }),
          keepalive: true
        })
      } catch (flushError) {
        queue.length = 0
        void flushError
        return
      } finally {
        suppressForwarding = false
      }

      if (response && response.status === 429) {
        restoreRateLimitedBatch(entries)
        scheduleRetry()
        return
      }

      if (!response || response.ok !== true) {
        queue.length = 0
        return
      }
    }
  } finally {
    flushing = false
  }
}

function wrapConsoleMethod(level) {
  const current = console[level]
  if (current && current[WRAPPED]) return

  const wrapped = (...args) => {
    if (typeof current === 'function') current.apply(console, args)
    try {
      enqueue(level, args)
    } catch (forwardError) {
      void forwardError
    }
  }

  wrapped[WRAPPED] = true
  console[level] = wrapped
}

function installClientLogForwarder() {
  if (process.env.NODE_ENV === 'test') return

  wrapConsoleMethod('error')
  wrapConsoleMethod('warn')
  wrapConsoleMethod('info')
  installPageLifecycleFlush()
}

export { installClientLogForwarder }

export default { installClientLogForwarder }
